import { type ReactNode } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/use-translation"

interface DrawerShellProps {
  title?: string
  sourceName?: string
  idLine?: string
  onClose: () => void
  footer: ReactNode
  children: ReactNode
}

export function DrawerShell({ title, sourceName, idLine, onClose, footer, children }: DrawerShellProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col bg-white overflow-hidden h-full">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          {title
            ? <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            : <div className="h-6 w-40 bg-gray-100 rounded animate-pulse" />}
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t("closeDetailsPanel")} className="h-8 w-8">
            <X size={16} />
          </Button>
        </div>
        {sourceName
          ? <p className="text-xs text-gray-400 mt-1">{t("fromSource", { sourceName })}</p>
          : <div className="h-3 mt-2 w-28 bg-gray-100 rounded animate-pulse" />}
        {idLine !== undefined
          ? <div className="flex items-center gap-3 mt-2"><span className="text-sm font-mono text-gray-500">{idLine}</span></div>
          : <div className="h-3.5 mt-2 w-24 bg-gray-100 rounded animate-pulse" />}
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {children}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
        {footer}
      </div>
    </div>
  )
}
